import { useEffect, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useMutation } from '@tanstack/react-query'
import { authAPI } from '../../api/client'
import { useAuthStore } from '../../store/authStore'

declare global {
  interface Window {
    google?: {
      accounts: {
        id: {
          initialize: (cfg: object) => void
          renderButton: (el: HTMLElement, cfg: object) => void
        }
      }
    }
    FB?: {
      init: (cfg: object) => void
      login: (cb: (r: { authResponse?: { accessToken: string } }) => void, cfg: object) => void
    }
  }
}

const GOOGLE_CLIENT_ID = import.meta.env.VITE_GOOGLE_CLIENT_ID as string | undefined
const FB_APP_ID = import.meta.env.VITE_FB_APP_ID as string | undefined

interface Props {
  defaultTab?: 'login' | 'register'
}

const H = ({ style, ...p }: React.HTMLAttributes<HTMLElement>) => (
  <span style={{ fontFamily: "'Space Grotesk', sans-serif", ...style }} {...p} />
)

export default function AuthPage({ defaultTab = 'login' }: Props) {
  const [tab, setTab] = useState<'login' | 'register'>(defaultTab)
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [name, setName] = useState('')
  const [showPassword, setShowPassword] = useState(false)
  const [oauthError, setOauthError] = useState<string | null>(null)
  const { setAuth } = useAuthStore()
  const navigate = useNavigate()
  const googleRef = useRef<HTMLDivElement>(null)

  // ── Mutations ──
  const login = useMutation({
    mutationFn: () => authAPI.login(email, password),
    onSuccess: ({ token, refreshToken, userId, role }) => {
      setAuth(token, refreshToken, userId, role)
      navigate(role === 'ADMIN' ? '/admin' : '/dashboard')
    },
  })

  const register = useMutation({
    mutationFn: () => authAPI.register(email, password, name),
    onSuccess: ({ token, refreshToken, userId }) => {
      setAuth(token, refreshToken, userId)
      navigate('/onboarding')
    },
  })

  const googleLogin = useMutation({
    mutationFn: (credential: string) => authAPI.googleLogin(credential),
    onSuccess: ({ token, refreshToken, userId, role }) => {
      setAuth(token, refreshToken, userId, role)
      navigate(role === 'ADMIN' ? '/admin' : '/dashboard')
    },
    onError: (e) => setOauthError((e as Error).message),
  })

  const facebookLogin = useMutation({
    mutationFn: (accessToken: string) => authAPI.facebookLogin(accessToken),
    onSuccess: ({ token, refreshToken, userId, role }) => {
      setAuth(token, refreshToken, userId, role)
      navigate(role === 'ADMIN' ? '/admin' : '/dashboard')
    },
    onError: (e) => setOauthError((e as Error).message),
  })

  // ── Google button ──
  useEffect(() => {
    if (!GOOGLE_CLIENT_ID || !googleRef.current) return
    function initGoogle() {
      if (!window.google || !googleRef.current) return
      window.google.accounts.id.initialize({
        client_id: GOOGLE_CLIENT_ID!,
        callback: (response: { credential: string }) => googleLogin.mutate(response.credential),
      })
      window.google.accounts.id.renderButton(googleRef.current, {
        theme: 'outline', size: 'large', text: 'continue_with', locale: 'vi', width: 360,
      })
    }
    if (window.google) { initGoogle() } else {
      const iv = setInterval(() => { if (window.google) { clearInterval(iv); initGoogle() } }, 200)
      return () => clearInterval(iv)
    }
  }, [tab])

  function handleFBLogin() {
    if (!FB_APP_ID) return
    if (!window.FB) {
      const s = document.createElement('script')
      s.src = 'https://connect.facebook.net/vi_VN/sdk.js'
      s.onload = () => { window.FB!.init({ appId: FB_APP_ID, version: 'v19.0' }); doFB() }
      document.head.appendChild(s)
    } else doFB()
  }
  function doFB() {
    window.FB!.login(r => {
      if (r.authResponse?.accessToken) facebookLogin.mutate(r.authResponse.accessToken)
      else setOauthError('Đăng nhập Facebook bị huỷ')
    }, { scope: 'email,public_profile' })
  }

  const isPending = login.isPending || register.isPending
  const mutationError = login.isError ? (login.error as Error).message : register.isError ? (register.error as Error).message : null

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    setOauthError(null)
    if (tab === 'login') login.mutate()
    else register.mutate()
  }

  return (
    <div style={{ minHeight: '100vh', display: 'flex', fontFamily: "'Manrope', sans-serif", background: '#f6f6ff', overflowX: 'hidden' }}>
      {/* ── Left: Visual column ── */}
      <section
        className="hidden lg:flex"
        style={{ width: '50%', minHeight: '100vh', position: 'relative', background: '#060e20', alignItems: 'center', justifyContent: 'center', overflow: 'hidden' }}
      >
        {/* BG overlay */}
        <div style={{ position: 'absolute', inset: 0, background: 'linear-gradient(135deg, rgba(0,75,226,0.3) 0%, rgba(142,0,213,0.2) 100%)', zIndex: 1 }} />
        <div style={{ position: 'absolute', top: '-10rem', left: '-5rem', width: '30rem', height: '30rem', borderRadius: '50%', background: 'rgba(0,75,226,0.12)', filter: 'blur(60px)' }} />
        <div style={{ position: 'absolute', bottom: '-8rem', right: '-5rem', width: '25rem', height: '25rem', borderRadius: '50%', background: 'rgba(142,0,213,0.12)', filter: 'blur(60px)' }} />

        <div style={{ position: 'relative', zIndex: 2, padding: '4rem', maxWidth: '32rem' }}>
          {/* Lock badge */}
          <div style={{ display: 'inline-flex', alignItems: 'center', gap: '0.5rem', padding: '0.5rem 1rem', borderRadius: '9999px', background: 'rgba(255,255,255,0.1)', backdropFilter: 'blur(12px)', marginBottom: '2rem' }}>
            <span className="material-symbols-outlined" style={{ color: '#809bff', fontSize: '1rem', fontVariationSettings: "'FILL' 1" }}>lock</span>
            <span style={{ fontSize: '0.6875rem', fontWeight: 800, letterSpacing: '0.12em', textTransform: 'uppercase', color: '#d1dcff' }}>Bảo mật cấp ngân hàng</span>
          </div>

          <H style={{ display: 'block', fontSize: 'clamp(3rem,5vw,5rem)', fontWeight: 900, letterSpacing: '-0.04em', lineHeight: 0.9, color: 'white', marginBottom: '1.5rem' }}>
            {tab === 'login' ? (
              <>Chào mừng<br />trở lại<br /><span style={{ background: 'linear-gradient(135deg,#809bff,#00e3fd)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' }}>Hustler.</span></>
            ) : (
              <>Tham gia<br />cuộc cách<br /><span style={{ background: 'linear-gradient(135deg,#809bff,#00e3fd)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' }}>mạng.</span></>
            )}
          </H>
          <p style={{ color: 'rgba(149,156,181,0.9)', fontSize: '1.0625rem', lineHeight: 1.7, maxWidth: '22rem' }}>
            {tab === 'login'
              ? 'Tiếp tục hành trình ngôn ngữ của bạn. An toàn, thích nghi và được xây dựng cho hustle hiện đại.'
              : 'Truy cập AI tutor cá nhân hoá và định nghĩa lại cách bạn chinh phục tiếng Anh.'}
          </p>

          {/* Decorative dots */}
          <div style={{ marginTop: '3rem', display: 'flex', gap: '0.75rem' }}>
            <div style={{ height: '3px', width: '3rem', borderRadius: '9999px', background: '#004be2' }} />
            <div style={{ height: '3px', width: '1rem', borderRadius: '9999px', background: 'rgba(165,173,198,0.2)' }} />
            <div style={{ height: '3px', width: '1rem', borderRadius: '9999px', background: 'rgba(165,173,198,0.2)' }} />
          </div>
        </div>

        {/* Kinetic ghost text */}
        <div style={{ position: 'absolute', bottom: '3rem', right: '2rem', display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: '0', opacity: 0.04 }}>
          <H style={{ fontSize: '6rem', fontWeight: 900, color: 'white', lineHeight: 1, userSelect: 'none' }}>AI TUTOR</H>
          <H style={{ fontSize: '6rem', fontWeight: 900, color: 'white', lineHeight: 1, userSelect: 'none' }}>ENGHUSTLE</H>
        </div>
      </section>

      {/* ── Right: Form column ── */}
      <section style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 'clamp(1.5rem, 5vw, 3rem) clamp(1rem, 4vw, 2rem)', background: 'white' }}>
        <div style={{ width: '100%', maxWidth: '27rem' }}>
          {/* Brand (mobile only) */}
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '2.5rem' }} className="lg:hidden">
            <div style={{ width: '2.25rem', height: '2.25rem', borderRadius: '0.75rem', background: 'linear-gradient(135deg,#004be2,#809bff)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <span className="material-symbols-outlined" style={{ color: 'white', fontSize: '1.25rem', fontVariationSettings: "'FILL' 1" }}>bolt</span>
            </div>
            <H style={{ fontWeight: 800, fontSize: '1.25rem', letterSpacing: '-0.03em', color: '#004be2' }}>EngHustle AI</H>
          </div>

          {/* Header with tab switch */}
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '2.5rem' }}>
            <div style={{ flex: 1 }}>
              <H style={{ display: 'block', fontSize: '2rem', fontWeight: 800, letterSpacing: '-0.03em', color: '#272e42', marginBottom: '0.25rem' }}>
                {tab === 'login' ? 'Đăng nhập' : 'Tạo tài khoản'}
              </H>
              <p style={{ color: '#535b71', fontSize: '0.9375rem' }}>
                {tab === 'login' ? 'Tiếp tục hành trình ngôn ngữ.' : 'Nâng cấp kỹ năng với AI thông minh.'}
              </p>
            </div>
            <button
              onClick={() => { setTab(t => t === 'login' ? 'register' : 'login'); setOauthError(null) }}
              style={{ fontSize: '0.875rem', fontWeight: 700, color: '#004be2', background: 'none', border: 'none', cursor: 'pointer', fontFamily: "'Space Grotesk',sans-serif", flexShrink: 0 }}
            >
              {tab === 'login' ? 'Tạo tài khoản →' : '← Đăng nhập'}
            </button>
          </div>

          {/* Social buttons */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.75rem', marginBottom: '1.75rem' }}>
            {/* Google */}
            {GOOGLE_CLIENT_ID ? (
              <div ref={googleRef} style={{ gridColumn: '1/-1' }} />
            ) : (
              <button
                onClick={() => setOauthError('Google login not configured')}
                style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.75rem', padding: '0.875rem 1rem', borderRadius: '0.75rem', background: '#eef0ff', border: 'none', cursor: 'pointer', fontFamily: "'Manrope',sans-serif", fontWeight: 700, fontSize: '0.875rem', color: '#272e42', transition: 'background 0.2s' }}
              >
                <svg width="18" height="18" viewBox="0 0 24 24"><path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#4285F4"/><path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/><path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/><path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/></svg>
                Google
              </button>
            )}
            {/* Facebook */}
            {FB_APP_ID && (
              <button
                onClick={handleFBLogin}
                disabled={facebookLogin.isPending}
                style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.75rem', padding: '0.875rem 1rem', borderRadius: '0.75rem', background: '#eef0ff', border: 'none', cursor: 'pointer', fontFamily: "'Manrope',sans-serif", fontWeight: 700, fontSize: '0.875rem', color: '#272e42' }}
              >
                <svg width="18" height="18" viewBox="0 0 24 24" fill="#1877F2"><path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/></svg>
                Facebook
              </button>
            )}
          </div>

          {/* Divider */}
          <div style={{ position: 'relative', display: 'flex', alignItems: 'center', marginBottom: '1.75rem' }}>
            <div style={{ flex: 1, height: '1px', background: 'rgba(165,173,198,0.2)' }} />
            <span style={{ padding: '0 1rem', fontSize: '0.6875rem', fontWeight: 800, letterSpacing: '0.12em', textTransform: 'uppercase', color: '#a5adc6' }}>
              {tab === 'login' ? 'Hoặc đăng nhập bằng email' : 'Hoặc đăng ký bằng email'}
            </span>
            <div style={{ flex: 1, height: '1px', background: 'rgba(165,173,198,0.2)' }} />
          </div>

          {/* Form */}
          <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: '1.125rem' }}>
            {tab === 'register' && (
              <div>
                <label style={{ display: 'block', fontSize: '0.6875rem', fontWeight: 800, color: '#535b71', textTransform: 'uppercase', letterSpacing: '0.1em', marginBottom: '0.5rem', marginLeft: '0.25rem' }}>
                  Họ và tên
                </label>
                <input
                  type="text"
                  value={name}
                  onChange={e => setName(e.target.value)}
                  placeholder="Nguyễn Văn A"
                  required
                  style={{ width: '100%', padding: '1rem 1.25rem', borderRadius: '0.75rem', background: '#eef0ff', border: 'none', outline: 'none', color: '#272e42', fontFamily: "'Manrope',sans-serif", fontSize: '16px', fontWeight: 500 }}
                  onFocus={e => { e.target.style.background = '#f6f6ff'; e.target.style.boxShadow = '0 0 0 2px #004be2' }}
                  onBlur={e => { e.target.style.background = '#eef0ff'; e.target.style.boxShadow = 'none' }}
                />
              </div>
            )}

            <div>
              <label style={{ display: 'block', fontSize: '0.6875rem', fontWeight: 800, color: '#535b71', textTransform: 'uppercase', letterSpacing: '0.1em', marginBottom: '0.5rem', marginLeft: '0.25rem' }}>
                Địa chỉ Email
              </label>
              <div style={{ position: 'relative' }}>
                <input
                  type="email"
                  value={email}
                  onChange={e => setEmail(e.target.value)}
                  placeholder="ten@example.com"
                  required
                  style={{ width: '100%', padding: '1rem 1.25rem', paddingRight: '3rem', borderRadius: '0.75rem', background: '#eef0ff', border: 'none', outline: 'none', color: '#272e42', fontFamily: "'Manrope',sans-serif", fontSize: '16px', fontWeight: 500 }}
                  onFocus={e => { e.target.style.background = '#f6f6ff'; e.target.style.boxShadow = '0 0 0 2px #004be2' }}
                  onBlur={e => { e.target.style.background = '#eef0ff'; e.target.style.boxShadow = 'none' }}
                />
                <span className="material-symbols-outlined" style={{ position: 'absolute', right: '1rem', top: '50%', transform: 'translateY(-50%)', color: '#a5adc6', fontSize: '1.25rem' }}>alternate_email</span>
              </div>
            </div>

            <div>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.5rem' }}>
                <label style={{ display: 'block', fontSize: '0.6875rem', fontWeight: 800, color: '#535b71', textTransform: 'uppercase', letterSpacing: '0.1em', marginLeft: '0.25rem' }}>
                  Mật khẩu
                </label>
                {tab === 'login' && (
                  <button type="button" style={{ fontSize: '0.8125rem', fontWeight: 700, color: '#004be2', background: 'none', border: 'none', cursor: 'pointer' }}>
                    Quên mật khẩu?
                  </button>
                )}
              </div>
              <div style={{ position: 'relative' }}>
                <input
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={e => setPassword(e.target.value)}
                  placeholder="••••••••"
                  minLength={tab === 'register' ? 8 : undefined}
                  required
                  style={{ width: '100%', padding: '1rem 1.25rem', paddingRight: '3rem', borderRadius: '0.75rem', background: '#eef0ff', border: 'none', outline: 'none', color: '#272e42', fontFamily: "'Manrope',sans-serif", fontSize: '16px', fontWeight: 500 }}
                  onFocus={e => { e.target.style.background = '#f6f6ff'; e.target.style.boxShadow = '0 0 0 2px #004be2' }}
                  onBlur={e => { e.target.style.background = '#eef0ff'; e.target.style.boxShadow = 'none' }}
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(v => !v)}
                  style={{ position: 'absolute', right: '1rem', top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', cursor: 'pointer', color: '#a5adc6' }}
                >
                  <span className="material-symbols-outlined" style={{ fontSize: '1.25rem' }}>{showPassword ? 'visibility_off' : 'visibility'}</span>
                </button>
              </div>
            </div>

            {/* Error */}
            {(mutationError || oauthError) && (
              <div style={{ padding: '0.875rem 1rem', borderRadius: '0.75rem', background: 'rgba(180,19,64,0.08)', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                <span className="material-symbols-outlined" style={{ color: '#b41340', fontSize: '1.125rem' }}>error</span>
                <span style={{ fontSize: '0.875rem', fontWeight: 600, color: '#b41340' }}>{mutationError || oauthError}</span>
              </div>
            )}

            {/* Submit */}
            <button
              type="submit"
              disabled={isPending}
              style={{
                display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.5rem',
                padding: '1.125rem', borderRadius: '9999px',
                background: isPending ? 'rgba(0,75,226,0.5)' : 'linear-gradient(135deg,#004be2,#809bff)',
                color: '#f2f1ff', fontFamily: "'Space Grotesk',sans-serif", fontWeight: 700, fontSize: '1rem',
                border: 'none', cursor: isPending ? 'not-allowed' : 'pointer',
                boxShadow: '0 8px 24px rgba(0,75,226,0.2)',
                transition: 'all 0.2s',
              }}
              onMouseEnter={e => { if (!isPending) { e.currentTarget.style.transform = 'translateY(-2px)'; e.currentTarget.style.boxShadow = '0 12px 32px rgba(0,75,226,0.35)' } }}
              onMouseLeave={e => { e.currentTarget.style.transform = 'none'; e.currentTarget.style.boxShadow = '0 8px 24px rgba(0,75,226,0.2)' }}
            >
              {isPending ? (
                <><span className="spinner" style={{ width: '1.25rem', height: '1.25rem', borderColor: 'rgba(255,255,255,0.3)', borderTopColor: 'white' }} /> Đang xử lý...</>
              ) : (
                <>{tab === 'login' ? 'Đăng nhập vào Workspace' : 'Bắt đầu hành trình'}
                  <span className="material-symbols-outlined" style={{ fontSize: '1.25rem' }}>arrow_forward</span>
                </>
              )}
            </button>
          </form>

          {/* Footer */}
          <p style={{ textAlign: 'center', marginTop: '2rem', fontSize: '0.875rem', color: '#535b71' }}>
            {tab === 'login' ? 'Chưa có tài khoản?' : 'Đã có tài khoản?'}
            {' '}
            <button
              onClick={() => { setTab(t => t === 'login' ? 'register' : 'login'); setOauthError(null) }}
              style={{ background: 'none', border: 'none', color: '#004be2', fontWeight: 700, cursor: 'pointer', fontFamily: "'Manrope',sans-serif", fontSize: '0.875rem' }}
            >
              {tab === 'login' ? 'Đăng ký miễn phí' : 'Đăng nhập ngay'}
            </button>
          </p>

          <p style={{ textAlign: 'center', marginTop: '2.5rem', fontSize: '0.75rem', color: '#a5adc6' }}>
            © 2026 EngHustle AI. Built for the Neon Pulse.
          </p>
        </div>
      </section>

      {/* Decorative blobs */}
      <div style={{ position: 'fixed', top: 0, right: 0, width: '24rem', height: '24rem', borderRadius: '50%', background: 'rgba(0,75,226,0.04)', filter: 'blur(80px)', pointerEvents: 'none' }} />
      <div style={{ position: 'fixed', bottom: 0, left: 0, width: '30rem', height: '30rem', borderRadius: '50%', background: 'rgba(142,0,213,0.04)', filter: 'blur(100px)', pointerEvents: 'none' }} />
    </div>
  )
}
